<!--
  Forms Landing Page Template
  ===========================
  
  Landing page for authenticated users displaying all available forms in a
  responsive grid layout. The interface adapts based on user permissions and
  screen size to provide optimal user experience.
  
  Layout Structure:
  - Header text explaining available actions based on user permissions
  - Responsive grid layout with form cards
  - Conditional button sections for admin users
  - Adaptive content based on screen size
  
  User Permission Behavior:
  - Regular Users: Clickable cards that navigate to form creation
  - Admin Users: Cards with dual buttons for creation and entry viewing
  
  Responsive Design:
  - Grid columns: 1 (mobile) → 2 (tablet) → 4 (desktop) → 4+ (large screens)
  - Button text: Short on mobile, full text on larger screens
  - Icons: Hidden on small screens, visible on medium+ screens
  
  Form Card Structure:
  - Form image/icon at the top
  - Form title in the center
  - Action buttons at the bottom (admin only)
  
  @author MacSafety Development Team
  @version 1.0
-->

<ion-content class="ion-padding">
  <!-- 
    User Instruction Header
    =======================
    Displays different instructions based on user permission level
    - Regular users see simplified instruction for form creation
    - Admin users see expanded instruction including entry viewing
  -->
  <ion-text *ngIf="!userIsAdmin()">
    <p>Select a form to create a new entry</p>
  </ion-text>
  <ion-text *ngIf="userIsAdmin()">
    <p>Select a form to view entries or create a new entry</p>
  </ion-text>
  
  <!-- 
    Forms Grid Layout
    =================
    Responsive grid that adapts from 1 column on mobile to 4+ columns on desktop
    Each form is displayed as a card with image, title, and conditional actions
  -->
  <ion-grid>
    <ion-row>
      <!-- 
        Form Card Column
        ================
        Responsive column sizing:
        - size="12": Full width on mobile (1 column)
        - size-md="6": Half width on medium screens (2 columns)
        - size-lg="4": One-third width on large screens (3 columns)
        - size-xl="3": One-quarter width on extra large screens (4+ columns)

    The initial columns are generated for each form in the forms array, 
    representing the standard set of forms with shared app features. 
    The final column displays the vehicle collision report, which is a unique, external form not integrated into the main app.  -->
      <ion-col size="12" size-md="6" size-lg="4" size-xl="3" *ngFor="let form of accessibleForms()">
        <!-- 
          Form Card Container
          ===================
          Card representing a single form with conditional click behavior
          - Regular users: Entire card is clickable for form creation
          - Admin users: Card is not clickable, buttons handle actions
        -->
        <ion-card 
          class="form-card ion-no-margin" 
          [class.clickable-card]="!userIsAdmin()"
          (click)="!userIsAdmin() ? createNew(form.formType) : null">
          
          <ion-card-content class="card-content ion-no-padding">
            
            <!-- 
              Form Image Section
              ===================
              Displays the form's card image at the top of each card
              Centered horizontally and vertically with padding
            -->
            <div class="image-section ion-padding-horizontal ion-padding-top ion-text-center">
              <ion-img 
                [src]="form.cardImage" 
                alt="{{ form.title }}"
                class="form-image ion-align-self-center"
              ></ion-img>
            </div>
            
            <!-- 
              Card Content Section
              ====================
              Contains the form title and action buttons
              Structured for consistent spacing and alignment
            -->
            <div class="content-section">
              <!-- 
                Form Title Header
                ==================
                Displays the form title in a centered card header
                No padding to allow custom spacing control
              -->
              <ion-card-header class="title-header ion-no-padding ion-padding-horizontal">
                <ion-card-title class="form-title ion-text-center">
                  <span class="title-text">{{ form.title }}</span>
                </ion-card-title>
              </ion-card-header>
              
              <!-- 
                Action Buttons Section
                =======================
                Dual button layout for admin users only
                - Primary button: Create new form entry
                - Secondary button: View existing entries
                
                Responsive button text:
                - Mobile: Short text ("New", "View")
                - Desktop: Full text ("Create new form", "View Entries")
                
                Responsive icons:
                - Hidden on small screens (ion-hide-md-up)
                - Visible on medium+ screens (ion-show-md)
              -->
              <div class="button-section ion-padding-horizontal ion-padding-bottom" *ngIf="userIsAdmin()">
                <!-- 
                  Create New Form Button
                  =======================
                  Primary action button for creating new form entries
                  Routes to individual form component for entry creation
                -->
                <ion-button 
                  fill="solid" 
                  expand="block" 
                  color="primary" 
                  size="small"
                  class="primary-button"
                  (click)="createNew(form.formType)">
                  <ion-icon name="add-circle-outline" slot="start" class="ion-show-md"></ion-icon>
                  <span class="ion-hide-md-up">New</span>
                  <span class="ion-hide-md-down">Create New Entry</span>
                </ion-button>
                
                <!-- 
                  View Entries Button
                  ====================
                  Secondary action button for viewing existing form entries
                  Routes to form entries list with form type filtering
                -->
                <ion-button 
                  fill="outline" 
                  expand="block" 
                  color="medium" 
                  size="small"
                  (click)="viewEntries(form.formType)">
                  <ion-icon name="list-outline" slot="start" class="ion-show-md"></ion-icon>
                  <span class="ion-hide-md-up">View</span>
                  <span class="ion-hide-md-down">View Entries</span>
                </ion-button>
              </div>
            </div>
            
          </ion-card-content>
        </ion-card>
      </ion-col>


          <ion-col size="12" size-md="6" size-lg="4" size-xl="3">
        <!-- 
          Form Card Container
          ===================
          Card representing a single form with conditional click behavior
          - Regular users: Entire card is clickable for form creation
          - Admin users: Card is not clickable, buttons handle actions
        -->
        <ion-card 
          class="form-card ion-no-margin" 
          [class.clickable-card]="!userIsAdmin()"
          (click)="!userIsAdmin() ? openVehicleCollisionForm() : null">
          
          <ion-card-content class="card-content ion-no-padding">
            
            <!-- 
              Form Image Section
              ===================
              Displays the form's card image at the top of each card
              Centered horizontally and vertically with padding
            -->
            <div class="image-section ion-padding-horizontal ion-padding-top ion-text-center">
              <ion-img 
                src="assets/form-card-tiles/vehicle-collision-report.png" 
                alt="Vehicle Collision Report"
                class="form-image ion-align-self-center"
              ></ion-img>
            </div>
            
            <!-- 
              Card Content Section
              ====================
              Contains the form title and action buttons
              Structured for consistent spacing and alignment
            -->
            <div class="content-section">
              <!-- 
                Form Title Header
                ==================
                Displays the form title in a centered card header
                No padding to allow custom spacing control
              -->
              <ion-card-header class="title-header ion-no-padding ion-padding-horizontal">
                <ion-card-title class="form-title ion-text-center">
                  <span class="title-text">Vehicle Collision Report</span>
                </ion-card-title>
              </ion-card-header>
              
              <!-- 
                Action Buttons Section
                =======================
                Dual button layout for admin users only
                - Primary button: Create new form entry
                
                Responsive button text:
                - Mobile: Short text ("New")
                - Desktop: Full text ("Create new form")
                
                Responsive icons:
                - Hidden on small screens (ion-hide-md-up)
                - Visible on medium+ screens (ion-show-md)
              -->
              <div class="button-section ion-padding-horizontal ion-padding-bottom" *ngIf="userIsAdmin()">
                <!-- 
                  Create New Form Button
                  =======================
                  Primary action button for creating new form entries
                  Routes to individual form component for entry creation
                -->
                <ion-button 
                  fill="solid" 
                  expand="block" 
                  color="primary" 
                  size="small"
                  class="primary-button"
                  (click)="openVehicleCollisionForm()">
                  <ion-icon name="add-circle-outline" slot="start" class="ion-show-md"></ion-icon>
                  <span class="ion-hide-md-up">New</span>
                  <span class="ion-hide-md-down">Create New Entry</span>
                </ion-button>
                
             
              </div>
            </div>
            
          </ion-card-content>
        </ion-card>
      </ion-col>
    </ion-row>
  </ion-grid>
</ion-content>